import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { StatusCode } from 'grpc-web'
import { DocumentSourcePromiseClient } from '@trustero/trustero-api-web/lib/documentsource/documentsource_grpc_web_pb'
import {
  DescribeSourceRequest,
  ListNodesRequest,
  SourceInfo,
} from '@trustero/trustero-api-web/lib/documentsource/documentsource_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { isGrpcError } from 'src/Utils/isGrpcError'
import { useHasNewDocumentPicker } from 'src/context/Gatekeeper'
import { hasDocumentSourceProvider } from './DocumentPicker.providers'
import {
  DocumentNode,
  ROOT_PARENT_ID,
  isContainerLike,
  nodeFromProto,
} from './DocumentPicker.types'

/** Identity and capabilities of a source; no provider call behind it. */
export const useDocumentSourceInfo = (
  receptorId: string | undefined,
  shouldFetch = true,
): GrpcResponse<SourceInfo> => {
  const request = new DescribeSourceRequest().setReceptorId(receptorId || '')
  const { response } = useSwrImmutableGrpc(
    DocumentSourcePromiseClient.prototype.describeSource,
    request,
    shouldFetch && !!receptorId,
  )
  return NewGrpcResponse(response)
}

// ---------------------------------------------------------------------------
// Problems: what the provider answered, in the picker's own terms.
// ---------------------------------------------------------------------------

export type PickerProblemKind =
  | 'rateLimit'
  | 'credentials'
  | 'noAccess'
  | 'notFound'
  | 'unavailable'
  | 'other'

export interface PickerProblem {
  kind: PickerProblemKind
  message: string
  /** Seconds the provider asked us to wait, for rateLimit. */
  retryAfterSeconds?: number
  /** Node the problem concerns, when it is one node rather than the source. */
  parentId?: string
}

const RETRY_AFTER_TRAILER = 'retry-after-seconds'

/** Classifies a DocumentSource failure. The gRPC code is set by ntrced from
 * the provider's HTTP status; a throttle also carries the wait in a trailer. */
export const classifyPickerError = (
  err: unknown,
  parentId?: string,
): PickerProblem => {
  if (!isGrpcError(err)) {
    return { kind: 'other', message: 'Something went wrong.', parentId }
  }
  const trailer = (err.metadata || {})[RETRY_AFTER_TRAILER]
  const retryAfterSeconds = trailer ? Number(trailer) : undefined
  switch (err.code) {
    case StatusCode.RESOURCE_EXHAUSTED:
      return {
        kind: 'rateLimit',
        message: 'The provider is rate limiting requests.',
        retryAfterSeconds:
          retryAfterSeconds && Number.isFinite(retryAfterSeconds)
            ? retryAfterSeconds
            : undefined,
        parentId,
      }
    case StatusCode.UNAUTHENTICATED:
      return {
        kind: 'credentials',
        message: 'The provider rejected this connection’s credentials.',
        parentId,
      }
    case StatusCode.PERMISSION_DENIED:
      return {
        kind: 'noAccess',
        message: 'The connection does not have access to this location.',
        parentId,
      }
    case StatusCode.NOT_FOUND:
      return {
        kind: 'notFound',
        message: 'This location no longer exists at the provider.',
        parentId,
      }
    case StatusCode.UNAVAILABLE:
    case StatusCode.DEADLINE_EXCEEDED:
      return {
        kind: 'unavailable',
        message: 'The provider did not answer in time.',
        parentId,
      }
    default:
      return { kind: 'other', message: err.message, parentId }
  }
}

// ---------------------------------------------------------------------------
// Tree state: flat and normalized. One level is loaded at a time, on demand.
// ---------------------------------------------------------------------------

export interface DocumentTreeState {
  nodesById: Record<string, DocumentNode>
  /** Child ids in listing order, keyed by parent id (ROOT_PARENT_ID for roots). */
  childrenOf: Record<string, string[]>
  /** Parents whose first page has been listed. */
  loaded: Record<string, true>
  /** Continuation token for parents with more pages. */
  nextToken: Record<string, string>
  /** Parents with a listing in flight. */
  loading: Record<string, true>
  problem: PickerProblem | null
  /** Ids matching the last server search, or null when not searching. */
  searchResults: string[] | null
  searching: boolean
}

type Action =
  | { type: 'reset' }
  | { type: 'loadStart'; parentId: string }
  | {
      type: 'loadDone'
      parentId: string
      nodes: DocumentNode[]
      nextToken: string
      append: boolean
    }
  | { type: 'loadFail'; parentId: string; problem: PickerProblem }
  | { type: 'searchStart' }
  | { type: 'searchDone'; nodes: DocumentNode[] }
  | { type: 'searchFail'; problem: PickerProblem }
  | { type: 'searchClear' }
  | { type: 'clearProblem' }

const emptyState: DocumentTreeState = {
  nodesById: {},
  childrenOf: {},
  loaded: {},
  nextToken: {},
  loading: {},
  problem: null,
  searchResults: null,
  searching: false,
}

const withoutKey = <T>(
  record: Record<string, T>,
  key: string,
): Record<string, T> => {
  if (!(key in record)) return record
  const next = { ...record }
  delete next[key]
  return next
}

const reducer = (
  state: DocumentTreeState,
  action: Action,
): DocumentTreeState => {
  switch (action.type) {
    case 'reset':
      return emptyState
    case 'loadStart':
      return {
        ...state,
        loading: { ...state.loading, [action.parentId]: true },
        problem:
          state.problem?.parentId === action.parentId ? null : state.problem,
      }
    case 'loadDone': {
      const nodesById = { ...state.nodesById }
      action.nodes.forEach((n) => {
        nodesById[n.id] = n
      })
      const ids = action.nodes.map((n) => n.id)
      const existing = action.append
        ? state.childrenOf[action.parentId] || []
        : []
      const seen = new Set(existing)
      const merged = [...existing, ...ids.filter((id) => !seen.has(id))]
      return {
        ...state,
        nodesById,
        childrenOf: { ...state.childrenOf, [action.parentId]: merged },
        loaded: { ...state.loaded, [action.parentId]: true },
        nextToken: action.nextToken
          ? { ...state.nextToken, [action.parentId]: action.nextToken }
          : withoutKey(state.nextToken, action.parentId),
        loading: withoutKey(state.loading, action.parentId),
      }
    }
    case 'loadFail':
      return {
        ...state,
        loading: withoutKey(state.loading, action.parentId),
        problem: action.problem,
      }
    case 'searchStart':
      return { ...state, searching: true, problem: null }
    case 'searchDone': {
      const nodesById = { ...state.nodesById }
      action.nodes.forEach((n) => {
        nodesById[n.id] = n
      })
      return {
        ...state,
        nodesById,
        searching: false,
        searchResults: action.nodes.map((n) => n.id),
      }
    }
    case 'searchFail':
      return { ...state, searching: false, problem: action.problem }
    case 'searchClear':
      if (state.searchResults === null && !state.searching) return state
      return { ...state, searching: false, searchResults: null }
    case 'clearProblem':
      return { ...state, problem: null }
    default:
      return state
  }
}

export interface DocumentTreeOptions {
  includeFiles: boolean
  byLabel?: boolean
}

export interface DocumentTree {
  state: DocumentTreeState
  /** Lists the first page under parentId, once; later calls are no-ops. */
  ensureLoaded: (parentId: string) => void
  /** Lists the next page under parentId. */
  loadMore: (parentId: string) => void
  /** Re-lists parentId from the first page (after an error). */
  reload: (parentId: string) => void
  /** Server-side search; "" clears. */
  search: (term: string) => Promise<void>
  reset: () => void
  childrenOf: (parentId: string) => DocumentNode[]
  /** Breadcrumb chain from a root down to nodeId. */
  pathTo: (nodeId: string) => DocumentNode[]
  /** Whether a node can be opened: provider hint until listed, then its children. */
  isExpandable: (node: DocumentNode) => boolean
  clearProblem: () => void
}

export const useDocumentTree = (
  receptorId: string | undefined,
  opts: DocumentTreeOptions,
): DocumentTree => {
  const client = useAuthorizedGrpcClient(DocumentSourcePromiseClient)
  const [state, dispatch] = useReducer(reducer, emptyState)
  const inFlight = useRef<Map<string, Promise<void>>>(new Map())
  // Stale answers (receptor switched, picker reset) must not land in state.
  const generation = useRef(0)
  const stateRef = useRef(state)
  stateRef.current = state

  const list = useCallback(
    async (
      parentId: string,
      pageToken: string,
      append: boolean,
    ): Promise<void> => {
      if (!receptorId) return
      const gen = generation.current
      const request = new ListNodesRequest()
        .setReceptorId(receptorId)
        .setParentId(parentId)
        .setPageToken(pageToken)
        .setIncludeFiles(opts.includeFiles)
        .setByLabel(!!opts.byLabel)
      dispatch({ type: 'loadStart', parentId })
      try {
        const response = await client.listNodes(request)
        if (gen !== generation.current) return
        dispatch({
          type: 'loadDone',
          parentId,
          nodes: response.getNodesList().map(nodeFromProto),
          nextToken: response.getNextPageToken(),
          append,
        })
      } catch (err) {
        if (gen !== generation.current) return
        dispatch({
          type: 'loadFail',
          parentId,
          problem: classifyPickerError(err, parentId),
        })
      }
    },
    [client, receptorId, opts.includeFiles, opts.byLabel],
  )

  const run = useCallback(
    (parentId: string, pageToken: string, append: boolean): void => {
      const key = `${parentId}\u001f${pageToken}`
      if (inFlight.current.has(key)) return
      const p = list(parentId, pageToken, append).finally(() => {
        inFlight.current.delete(key)
      })
      inFlight.current.set(key, p)
    },
    [list],
  )

  const ensureLoaded = useCallback(
    (parentId: string): void => {
      if (stateRef.current.loaded[parentId]) return
      run(parentId, '', false)
    },
    [run],
  )

  const loadMore = useCallback(
    (parentId: string): void => {
      const token = stateRef.current.nextToken[parentId]
      if (!token) return
      run(parentId, token, true)
    },
    [run],
  )

  const reload = useCallback(
    (parentId: string): void => {
      run(parentId, '', false)
    },
    [run],
  )

  const search = useCallback(
    async (term: string): Promise<void> => {
      const trimmed = term.trim()
      if (!trimmed) {
        dispatch({ type: 'searchClear' })
        return
      }
      if (!receptorId) return
      const gen = generation.current
      dispatch({ type: 'searchStart' })
      try {
        const response = await client.listNodes(
          new ListNodesRequest()
            .setReceptorId(receptorId)
            .setSearch(trimmed)
            .setIncludeFiles(opts.includeFiles),
        )
        if (gen !== generation.current) return
        dispatch({
          type: 'searchDone',
          nodes: response.getNodesList().map(nodeFromProto),
        })
      } catch (err) {
        if (gen !== generation.current) return
        dispatch({ type: 'searchFail', problem: classifyPickerError(err) })
      }
    },
    [client, receptorId, opts.includeFiles],
  )

  const reset = useCallback((): void => {
    generation.current += 1
    inFlight.current.clear()
    // ensureLoaded may run in the same effect flush as this reset (a
    // receptor switch). It reads the ref, which would otherwise still say
    // the previous receptor's root is loaded, and skip listing the new one.
    stateRef.current = emptyState
    dispatch({ type: 'reset' })
  }, [])

  // A different receptor or listing shape is a different tree.
  useEffect(() => {
    reset()
  }, [receptorId, opts.includeFiles, opts.byLabel, reset])

  const childrenOf = useCallback(
    (parentId: string): DocumentNode[] =>
      (state.childrenOf[parentId] || [])
        .map((id) => state.nodesById[id])
        .filter((n): n is DocumentNode => !!n),
    [state.childrenOf, state.nodesById],
  )

  const pathTo = useCallback(
    (nodeId: string): DocumentNode[] => {
      const path: DocumentNode[] = []
      let cur: DocumentNode | undefined = state.nodesById[nodeId]
      let guard = 0
      while (cur && guard < 200) {
        path.push(cur)
        cur =
          cur.parentId === ROOT_PARENT_ID
            ? undefined
            : state.nodesById[cur.parentId]
        guard += 1
      }
      return path.reverse()
    },
    [state.nodesById],
  )

  const isExpandable = useCallback(
    (node: DocumentNode): boolean => {
      if (!isContainerLike(node)) return false
      if (state.loaded[node.id]) {
        return (
          (state.childrenOf[node.id] || []).length > 0 ||
          !!state.nextToken[node.id]
        )
      }
      return node.hasChildren
    },
    [state.loaded, state.childrenOf, state.nextToken],
  )

  const clearProblem = useCallback(
    (): void => dispatch({ type: 'clearProblem' }),
    [],
  )

  return useMemo(
    () => ({
      state,
      ensureLoaded,
      loadMore,
      reload,
      search,
      reset,
      childrenOf,
      pathTo,
      isExpandable,
      clearProblem,
    }),
    [
      state,
      ensureLoaded,
      loadMore,
      reload,
      search,
      reset,
      childrenOf,
      pathTo,
      isExpandable,
      clearProblem,
    ],
  )
}

/**
 * Whether this account picks documents of a model through the document
 * picker: the model has a provider and the account is on the new picker.
 * Off, the entry points open the vendor's own picker.
 */
export const useDocumentPickerFor = (): ((modelId: string) => boolean) => {
  const enabled = useHasNewDocumentPicker()
  return useCallback(
    (modelId: string) => enabled && hasDocumentSourceProvider(modelId),
    [enabled],
  )
}
